import type { ReportExport, ReportExportStatus } from '@jaf/contracts';
import { useQuery } from '@tanstack/react-query';
import { Download, FileDown, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useSession } from '@/features/auth';
import { formatInstantDateTime } from '@/shared/lib/date';
import { Badge } from '@/shared/ui/primitives/badge';
import { Button } from '@/shared/ui/primitives/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/primitives/popover';
import { isUnfinished, reportExportsQuery } from '../api/report-exports-queries';
import { useDownloadReportExport } from '../hooks/use-download-report-export';
import { useAutoDownloadReportExports } from '../hooks/use-auto-download-report-exports';

const STATUS_LABELS: Readonly<Record<ReportExportStatus, string>> = {
  PENDING: 'Na fila',
  PROCESSING: 'Gerando',
  READY: 'Pronto para baixar',
  FAILED: 'Falhou',
};

function ExportItem({
  item,
  onDownload,
}: {
  item: ReportExport;
  onDownload: (item: ReportExport) => void;
}): ReactNode {
  return (
    <li className="flex items-center justify-between gap-3 py-2">
      <div className="min-w-0 space-y-0.5">
        <p className="truncate text-sm font-medium" title={item.fileName}>
          {item.fileName}
        </p>
        <p className="text-xs text-muted-foreground">
          {formatInstantDateTime(item.requestedAt)} · <span>{STATUS_LABELS[item.status]}</span>
          {item.status === 'FAILED' && item.failureMessage !== null && (
            <span className="block">{item.failureMessage}</span>
          )}
        </p>
      </div>
      {item.status === 'READY' ? (
        <Button
          variant="outline"
          size="sm"
          aria-label={`Baixar ${item.fileName}`}
          onClick={() => {
            onDownload(item);
          }}
        >
          <Download aria-hidden className="size-4" />
          Baixar
        </Button>
      ) : (
        isUnfinished(item) && (
          <Loader2
            aria-label="Em andamento"
            className="size-4 animate-spin text-muted-foreground"
          />
        )
      )}
    </li>
  );
}

/**
 * "Minhas exportações" no cabeçalho: as exportações em andamento e as que falharam. Acompanha a
 * fila e baixa cada arquivo sozinho assim que fica pronto; o botão Baixar fica para o caso de o
 * download automático falhar.
 */
export function ReportExportsMenu(): ReactNode {
  const organizationId = useSession((state) => state.session?.organizationId ?? '');
  const download = useDownloadReportExport();
  const result = useQuery({
    ...reportExportsQuery(organizationId),
    enabled: organizationId !== '',
  });
  const items = result.data?.items;
  useAutoDownloadReportExports(items, download);
  const running = items?.filter(isUnfinished).length ?? 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm">
          <FileDown aria-hidden className="size-4" />
          Exportações
          {running > 0 && (
            <Badge variant="secondary" aria-label={`${String(running)} em andamento`}>
              {running}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96">
        <h2 className="text-sm font-semibold">Minhas exportações</h2>
        {items === undefined || items.length === 0 ? (
          <p className="pt-2 text-sm text-muted-foreground">
            {result.isError
              ? 'Não foi possível carregar as exportações.'
              : 'Nenhuma exportação em andamento. Os arquivos não ficam guardados: são baixados assim que ficam prontos.'}
          </p>
        ) : (
          <ul className="divide-y">
            {items.map((item) => (
              <ExportItem
                key={item.id}
                item={item}
                onDownload={(target) => {
                  void download(target);
                }}
              />
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
